import { formatAmount } from '@shared/lib/format';
import { cn } from '@shared/lib/cn';
import { Icon } from '@shared/ui/icon/Icon';
import type { Receipt } from '@features/receipts';

/** Formatte une date en temps relatif court ("il y a 2 h", "hier", "3 j"). */
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  const diffH = Math.round(diffMin / 60);
  const diffDays = Math.round(diffH / 24);

  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  if (diffH < 24) return `il y a ${diffH} h`;
  if (diffDays === 1) return 'hier';
  if (diffDays < 7) return `il y a ${diffDays} j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function statusMeta(status: Receipt['status']): {
  icon: 'check-circle' | 'sparkles';
  tone: string;
} {
  return status === 'validated'
    ? { icon: 'check-circle', tone: 'bg-emerald-50 text-emerald-600' }
    : { icon: 'sparkles', tone: 'bg-amber-50 text-amber-600' };
}

/**
 * « Recent Activity » — flux des derniers tickets traités, triés du
 * plus récent au plus ancien, avec statut et montant.
 *
 * Note : utilise `receipt.date` (date du ticket) comme repère temporel ;
 * si un champ `createdAt`/`updatedAt` existe côté API, le substituer ici
 * donnerait un flux plus fidèle à l'activité réelle (upload/traitement)
 * plutôt qu'à la date de la dépense elle-même.
 */
export function RecentActivityFeed({
  receipts,
  limit = 6,
}: {
  receipts: Receipt[];
  limit?: number;
}) {
  const recent = [...receipts]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, limit);

  if (recent.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-slate-400">
        Aucune activité récente.
      </div>
    );
  }

  return (
    <ul className="flex flex-col">
      {recent.map((receipt, index) => {
        const meta = statusMeta(receipt.status);
        return (
          <li
            key={receipt.id}
            className={cn(
              'flex items-center gap-3 py-2.5',
              index !== recent.length - 1 && 'border-b border-slate-100',
            )}
          >
            <span
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-full',
                meta.tone,
              )}
            >
              <Icon name={meta.icon} size={14} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-900">{receipt.merchant}</p>
              <p className="text-xs text-slate-400">{formatRelativeTime(receipt.createdAt)}</p>
            </div>
            <span className="shrink-0 text-sm font-semibold text-slate-900">
              {formatAmount(receipt.total, receipt.currency)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
